import { Box, Button, Checkbox, Divider, FormControlLabel, MenuItem, Select, Stack, Typography } from "@mui/material";
import { useMemo } from "react";
import type { CurrencyDimensionMeta } from "../../../../../../api/types/svTypes.ts";
import FormatSelect from "../../../sv/components/builder/common/FormatSelect.tsx";
import { DimensionNames } from "../../../sv/utilities/DimensionNames.ts";
import useBalanceSheetContext from "../contexts/BalanceSheetContext.tsx";
import DimensionValuePicker from "./DimensionValuePicker.tsx";

const standardReportCurrencyDimensions: CurrencyDimensionMeta[] = [
  {
    name: DimensionNames.EntityCurrencyCode,
    caption: "Entity Currency Code",
  },
];

const BalanceSheetSettingsPanel = () => {
  const {
    numberFormat,
    onNumberFormatChange,
    bcDimensions,
    selectedBreakDownDimensionName,
    onBreakDownDimensionChange,
    dimensionValues,
    isDimensionValuesLoading,
    selectedDimensionValues,
    onSelectedDimensionValuesChange,
    hideOtherDimension,
    onHideOtherDimensionChange,
    hideUnassignedDimension,
    onHideUnassignedDimensionChange,
    isSettingsDefault,
    onResetSettings,
  } = useBalanceSheetContext();

  const selectedDimension = useMemo(
    () => bcDimensions.find((d) => d.name === selectedBreakDownDimensionName) ?? null,
    [bcDimensions, selectedBreakDownDimensionName]
  );

  return (
    <Stack spacing={2} sx={{ width: 300 }}>
      <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between" }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Settings
        </Typography>
        <Button
          variant="text"
          size="small"
          color="secondary"
          disabled={isSettingsDefault}
          onClick={onResetSettings}
          sx={{ textTransform: "none" }}
        >
          Reset All
        </Button>
      </Stack>
      <Stack spacing={1.5} sx={{ flex: 1 }}>
        <Stack spacing={1} direction="row" sx={{ alignItems: "center", flex: 1, width: "100%" }}>
          <Typography variant="body1" sx={{ whiteSpace: "nowrap", width: "100px" }}>
            Number Format
          </Typography>
          <FormatSelect
            format={numberFormat}
            onUpdateFormatter={onNumberFormatChange}
            currencyDimensions={standardReportCurrencyDimensions}
            disabled={false}
          />
        </Stack>
        <Divider sx={{ borderStyle: "dashed" }} />
        <Stack spacing={1} direction="row" sx={{ alignItems: "center", flex: 1 }}>
          <Typography variant="body1" sx={{ width: "100px" }}>
            Break Down by
          </Typography>
          <Select
            size="small"
            displayEmpty
            value={selectedBreakDownDimensionName ?? ""}
            sx={{ flex: 1, ".MuiSelect-icon": { color: "secondary.main" } }}
            onChange={(e) => onBreakDownDimensionChange(e.target.value || undefined)}
          >
            <MenuItem value="">None</MenuItem>
            {bcDimensions.map((d) => (
              <MenuItem key={d.name} value={d.name}>
                {d.caption}
              </MenuItem>
            ))}
          </Select>
        </Stack>
        {selectedDimension && (
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <DimensionValuePicker
              key={selectedDimension.name}
              dimension={selectedDimension}
              values={dimensionValues}
              loading={isDimensionValuesLoading}
              selectedValues={selectedDimensionValues}
              onSelectedValuesChange={onSelectedDimensionValuesChange}
            />
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Checkbox checked={hideOtherDimension} onChange={(_, checked) => onHideOtherDimensionChange(checked)} />
              }
              label={`Hide Other ${selectedDimension.caption}`}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={hideUnassignedDimension}
                  onChange={(_, checked) => onHideUnassignedDimensionChange(checked)}
                />
              }
              label={`Hide Unassigned ${selectedDimension.caption}`}
            />
          </Box>
        )}
        <Divider sx={{ borderStyle: "dashed" }} />
      </Stack>
    </Stack>
  );
};

export default BalanceSheetSettingsPanel;
